import React, { useState } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, Clock, Download } from 'lucide-react';
import api from '../utils/api';

interface QuizUploadProps {
  onUploadSuccess: () => void;
}

interface QuizPreview {
  title: string;
  subject: string;
  description?: string;
  timeLimit?: number;
  questions: any[];
}

const QuizUpload: React.FC<QuizUploadProps> = ({ onUploadSuccess }) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [timeLimit, setTimeLimit] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<QuizPreview | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const subjects = [
    { value: 'VOCABULARY', label: 'Vocabulary' },
    { value: 'CLOZE', label: 'Cloze' },
    { value: 'COMPREHENSION', label: 'Comprehension (MCQ)' },
    { value: 'MATHS', label: 'Maths (MCQ)' }
  ];

  const handleCsvChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCsvFile(file);
    setError('');

    // Preview CSV content
    try {
      const text = await file.text();
      const lines = text.split('\n');
      const header = lines[0];
      const dataLines = lines.slice(1).filter(line => line.trim());

      if (!header.includes('questionText') || !header.includes('correctAnswer')) {
        setError('Invalid CSV format. Required columns: questionType, questionText, correctAnswer');
        return;
      }

      const questions = dataLines.map((line, index) => {
        const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const [id, questionType, questionText, choiceA, choiceB, choiceC, choiceD, correctAnswer, explanation, mediaFile] = values;
        
        return {
          order: index + 1,
          questionType: questionType || 'MCQ',
          questionText: questionText || `Question ${index + 1}`,
          choiceA,
          choiceB,
          choiceC,
          choiceD,
          correctAnswer: correctAnswer || '',
          explanation,
          mediaFile
        };
      });

      setPreview({
        title: title || `Quiz ${Date.now()}`,
        subject: subject || 'VOCABULARY',
        description,
        timeLimit: timeLimit ? parseInt(timeLimit) : undefined,
        questions
      });
    } catch (error) {
      setError('Failed to parse CSV file');
    }
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setMediaFiles(files);
  };

  const handleUpload = async () => {
    if (!csvFile || !title || !subject) {
      setError('Please provide CSV file, title, and subject');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('csvFile', csvFile);
      formData.append('title', title);
      formData.append('subject', subject);
      if (description) formData.append('description', description);
      if (timeLimit) formData.append('timeLimit', timeLimit);

      // Add media files
      mediaFiles.forEach(file => {
        formData.append('mediaFiles', file);
      });

      await api.post('/quizzes/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000 // large uploads can exceed the default timeout
      });

      // Reset form
      setCsvFile(null);
      setMediaFiles([]);
      setTitle('');
      setSubject('');
      setDescription('');
      setTimeLimit('');
      setPreview(null);
      setShowPreview(false);

      onUploadSuccess();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to upload quiz');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = [
      'id,questionType,questionText,choiceA,choiceB,choiceC,choiceD,correctAnswer,explanation,mediaFile',
      '1,MCQ,"What is 2+2?",2,3,4,5,4,"Basic addition",',
      '2,TYPE_IN,"What is the capital of France?",,,,,Paris,"Capital city",',
      '3,MCQ,"Choose the correct spelling",there,their,they\'re,thier,their,"Possessive form",image1.jpg'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'quiz-template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4">How to Upload a Quiz</h3>
        <div className="space-y-2 text-blue-800 dark:text-blue-200">
          <p>1. Download the CSV template and fill in your questions</p>
          <p>2. Include any media files (images, audio, PDFs) referenced in your CSV</p>
          <p>3. Fill in the quiz details below and upload both CSV and media files</p>
          <p>4. Review the preview and submit</p>
        </div>

        <button
          onClick={downloadTemplate}
          className="mt-4 flex items-center text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-400 font-medium"
        >
          <Download className="w-4 h-4 mr-2" />
          Download CSV Template
        </button>
      </div>

      {/* Upload Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">Quiz Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quiz Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input w-full"
              placeholder="Enter quiz title"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subject *
            </label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="input w-full"
              required
            >
              <option value="">Select subject</option>
              {subjects.map(sub => (
                <option key={sub.value} value={sub.value}>{sub.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input w-full"
            rows={3}
            placeholder="Optional description of the quiz"
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Clock className="w-4 h-4 inline mr-1" />
            Time Limit (seconds)
          </label>
          <input
            type="number"
            value={timeLimit}
            onChange={(e) => setTimeLimit(e.target.value)}
            className="input w-full md:w-48"
            placeholder="Leave blank for untimed"
            min="30"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Leave blank for untimed quiz. Minimum 30 seconds.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quiz CSV File *
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvChange}
                className="hidden"
                id="csv-upload"
                required
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <div className="text-center">
                  <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {csvFile ? csvFile.name : 'Click to upload CSV file'}
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Media Files (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,audio/mpeg,audio/wav"
                multiple
                onChange={handleMediaChange}
                className="hidden"
                id="media-upload"
              />
              <label htmlFor="media-upload" className="cursor-pointer">
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {mediaFiles.length > 0
                      ? `${mediaFiles.length} file(s) selected`
                      : 'Click to upload media files'
                    }
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>
        
        {/* Preview Button */}
        {preview && (
          <div className="mb-6">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="btn btn-outline"
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}
        
        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={loading || !csvFile || !title || !subject}
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Quiz
            </>
          )}
        </button>
      </div>
      
      {/* Preview */}
      {showPreview && preview && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quiz Preview</h3>

          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">{preview.title}</h4>
            <p className="text-sm text-gray-600 dark:text-gray-300">Subject: {preview.subject}</p>
            {preview.description && <p className="text-sm text-gray-600 dark:text-gray-300">{preview.description}</p>}
            {preview.timeLimit && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Time Limit: {Math.floor(preview.timeLimit / 60)}:{(preview.timeLimit % 60).toString().padStart(2, '0')}
              </p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-300">{preview.questions.length} questions</p>
          </div>

          <div className="space-y-4 max-h-64 overflow-y-auto">
            {preview.questions.slice(0, 5).map((question, index) => (
              <div key={index} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Q{question.order}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{question.questionType}</span>
                </div>

                <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">{question.questionText}</p>

                {question.questionType === 'MCQ' && (
                  <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                    {['A', 'B', 'C', 'D'].map(letter => {
                      const choice = question[`choice${letter}`];
                      return choice ? (
                        <p key={letter} className={letter === 'A' && question.correctAnswer === choice ? 'text-green-600 font-medium' : ''}>
                          {letter}. {choice}
                        </p>
                      ) : null;
                    })}
                  </div>
                )}

                {question.questionType === 'TYPE_IN' && (
                  <p className="text-sm text-green-600 font-medium">
                    Correct Answer: {question.correctAnswer}
                  </p>
                )}

                {question.explanation && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Explanation: {question.explanation}
                  </p>
                )}

                {question.mediaFile && (
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                    📎 Media: {question.mediaFile}
                  </p>
                )}
              </div>
            ))}

            {preview.questions.length > 5 && (
              <p className="text-center text-gray-500 dark:text-gray-400">
                ... and {preview.questions.length - 5} more questions
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizUpload;